import { model as router } from '!/router'
import { exists, nay, T } from '@/helpers'
import { logger } from '@/logger'
import { digits, maxLength, minLength, required } from '@/validators'
import { api } from '@setplex/tria-api'
import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
  type Store,
} from 'effector'
import { createForm } from 'effector-forms'
import { debounce } from 'patronum/debounce'
import { humanizeTo, retoken, untoken } from '../../shared/tools'

export const MAX_CODE_REQUESTS = 5

// init page
export const init = createEvent()

// request new reset password code
export const resendNewResetPasswordCode = createEvent()

// parse data from location hash
export const parsed = createEvent<{
  id: string // registration uuid to confirm code
  from: number // `id` request time
  to: number // end of life of `id`
}>()

type State = {
  email: string
  codecnt?: number
  failcnt?: number
}

// get data from location state
export const stated = createEvent<State>()

// confirm code request effect
export const verifyResetPasswordCodeFx = attach({
  effect: api.subscriber.verifyResetPasswordCodeFx,
})

// resend reset password code effect
export const resendResetPasswordCodeFx = attach({
  effect: api.subscriber.resendResetPasswordCodeFx,
})

// effect to create new hash token
const retokenFx = createEffect<{ id: string; to: number }, string>(
  ({ id, to }) => retoken(id, to)
)

// effect to create hash token
const untokenFx = createEffect<
  string,
  { id: string; from: number; to: number }
>((token) => untoken(token))

// reset password session uuid
export const $id = createStore<string | null>(null) //
  .on(parsed, (__, { id }) => id)

// reset password session uuid request time
export const $from = createStore<number | null>(null) //
  .on(parsed, (__, { from }) => from)

// reset password session uuid end of life
export const $to = createStore<number | null>(null) //
  .on(parsed, (__, { to }) => to)

// reset password session email
export const $email = createStore<string | null>(null) //
  .on(stated, (__, { email }) => email ?? null)

// counter of requested codes
export const $codeCounter = createStore<number>(0) //
  .reset(init)
  .on(stated, (__, { codecnt }) => codecnt ?? 0)
  .on(api.events.logic.tooManyCodeRequests, () => MAX_CODE_REQUESTS)
  .on(api.events.logic.noMoreCodeRequestsAllowed, () => MAX_CODE_REQUESTS)

// counter of failed code enters
export const $failedCounter = createStore<number>(0) //
  .reset(init)
  .on(stated, (__, { failcnt }) => failcnt ?? 0)
  .on(api.events.logic.resetPasswordCodeInvalid, (x) => x + 1)
  .on(api.events.logic.noMoreConfirmationAttempts, () => 5)

// error to show to the user
export const $error = createStore<string | null>(null)

// debounced error, in order to prevent react to render component with old data
export const $viewError = createStore<string | null>(null)

// permanent error, blocking reset password process
export const $permError = createStore<string | null>(null)

// lock interface after successful password reset
export const $locked = createStore<boolean>(false)
  .reset(init)
  .on(verifyResetPasswordCodeFx.done, T)

// confirm code form
export const confirmForm = createForm({
  fields: {
    code: {
      init: '' as string,
      rules: [required(), digits(), maxLength(6), minLength(6)],
    },
  },
  validateOn: ['submit'],
})

// add some magic -> autovalidate and submit form when user types 6 chars
sample({
  clock: confirmForm.fields.code.$value,
  filter: (code) => code?.length >= 6,
  target: confirmForm.validate,
})

// send verify code request
sample({
  clock: confirmForm.formValidated,
  source: $id,
  filter: Boolean,
  fn: (id, { code }) => ({ id, code }),
  target: verifyResetPasswordCodeFx,
})

// reset form on init route
sample({
  clock: init,
  target: confirmForm.reset,
})

// decode hash token to get `id` and `id`s end of life
sample({
  clock: init,
  source: router.$hash,
  filter: Boolean,
  fn: (hash) => hash.replace(/^#?/, ''),
  target: untokenFx,
})

// in case there is no hash -> redirect back to /forgot-password
sample({
  clock: init,
  source: router.$hash,
  filter: (hash) => nay(exists(hash)),
  fn: () => ({ pathname: '/forgot-password' }),
  target: router.navigateReplace,
})

// in case of failed to decode hash -> redirect back to /forgot-password
sample({
  clock: untokenFx.fail,
  fn: () => ({ pathname: '/forgot-password' }),
  target: router.navigateReplace,
})

// in case we could not decode token -> log error
sample({
  clock: untokenFx.fail,
  fn: (fail) => ['could not decode token:', fail],
  target: logger.errorFx,
})

// get `id` and `id`s end of life
sample({
  clock: untokenFx.doneData,
  target: parsed,
})

// get `email` from history state
// this will get preserved in browser, but if user shared his verify link -> state would be empty
sample({
  clock: init,
  source: router.$state as Store<State | null>,
  filter: Boolean,
  target: stated,
})

// request new confirmation code
sample({
  clock: resendNewResetPasswordCode,
  source: $id,
  filter: Boolean,
  fn: (id) => ({ id }),
  target: resendResetPasswordCodeFx,
})

// regenerate token to shift start time
sample({
  clock: resendResetPasswordCodeFx,
  source: { id: $id, to: $to },
  filter: ({ id, to }) => exists(id) && exists(to),
  fn: ({ id, to }) => ({ id: id!, to: to! }),
  target: retokenFx,
})

// in case we could not encode token -> log error
sample({
  clock: retokenFx.fail,
  fn: (fail) => ['could not encode new token:', fail],
  target: logger.errorFx,
})

// redirect to verify page (to replace token)
sample({
  clock: retokenFx.doneData,
  source: {
    location: router.$location,
    codecnt: $codeCounter,
    failcnt: $failedCounter,
  },
  filter: ({ location }) => exists(location),
  fn: ({ location, codecnt, failcnt }, token) => ({
    ...location,
    hash: token,
    state: {
      ...(location!.state as State),
      codecnt: codecnt + 1,
      failcnt,
    },
  }),
  target: router.navigateReplace,
})

// redirect to set new password page after successful email confirmation
sample({
  clock: verifyResetPasswordCodeFx.done,
  source: { email: $email, hash: router.$hash },
  filter: ({ hash }) => exists(hash),
  fn: ({ email, hash }, { params }) => ({
    pathname: '/forgot-password/new',
    hash: hash!.replace(/^#?/, ''),
    state: { ...params, email },
  }),
  target: router.navigatePush,
})
sample({
  clock: verifyResetPasswordCodeFx.fail,
  fn: () => '',
  target: confirmForm.fields.code.$value,
})

//
// Permanent errors -> redirect to /fail page
//

sample({
  clock: $failedCounter,
  filter: (count) => count >= 5,
  fn: () => ({
    pathname: '/forgot-password/fail',
    state: 'code_request_attempt_error',
  }),
  target: router.navigateReplace,
})

sample({
  clock: api.events.logic.resetPasswordCodeSessionExpired,
  fn: () => ({
    pathname: '/forgot-password/fail',
    state: 'reset_password_request_expired_error',
  }),
  target: router.navigateReplace,
})

sample({
  clock: parsed,
  filter: ({ to }) => humanizeTo(to) === null,
  fn: () => ({
    pathname: '/forgot-password/fail',
    state: 'reset_password_request_expired_error',
  }),
  target: router.navigateReplace,
})

sample({
  clock: api.events.logic.sessionInvalid,
  fn: () => ({
    pathname: '/forgot-password/fail',
    state: 'session_invalid_error',
  }),
  target: router.navigateReplace,
})

//
// add listeners for server errors
//

$permError
  .reset(init)
  .on(parsed, (__, { to }) =>
    humanizeTo(to) === null ? 'reset_password_request_expired_error' : null
  )
  .on(
    api.events.logic.resetPasswordCodeSessionExpired, // WG0046 - Reset password session has expired
    () => 'reset_password_request_expired_error'
  )
  .on(
    api.events.logic.noMoreConfirmationAttempts, // WG0047 - No more confirmation attempts are allowed
    () => 'confirm_fail_attempt_error'
  )
  .on(
    api.events.logic.noMoreCodeRequestsAllowed, // WG0048 | WG0067
    () => 'code_request_attempt_error'
  )

$error
  .reset(
    confirmForm.fields.code.changed,
    init,

    // "server validation" errors
    api.events.logic.accountIpACLLock // handled by geoblock navigation sub-process
  )
  .on(
    api.events.logic.sessionInvalid, // WG0044 - Session is invalid
    () => 'session_invalid_error'
  )
  .on(
    api.events.logic.resetPasswordCodeInvalid, // WG0045 - Reset password code is invalid
    () => 'invalid_sms_code_error'
  )
  .on(
    api.events.logic.resetPasswordCodeSessionExpired, // WG0046 - Reset password session has expired
    () => 'reset_password_request_expired_error'
  )
  .on(
    api.events.logic.noMoreConfirmationAttempts, // WG0047 - No more confirmation attempts are allowed
    () => 'confirm_fail_attempt_error'
  )
  .on(
    api.events.logic.noMoreCodeRequestsAllowed, // WG0048 | WG0067 - No more new code requests allowed
    () => 'code_request_attempt_error'
  )
  .on(
    api.events.logic.tooManyCodeRequests, // WG0065 | WG0070 - Too many new code requests
    () => 'code_request_attempt_error'
  )

  // some generic request errors
  .on(
    api.events.http.httpError, // any non-200 error
    (error) => (error === null ? 'unknown_error' : error)
  )
  .on(
    api.events.http.error, // any request error (dns, carrier, etc.)
    (error) => (error === null ? 'request_failed_error' : error)
  )

debounce({
  source: $error,
  timeout: 50,
  target: $viewError,
})

$viewError.reset(confirmForm.fields.code.changed, init)
